import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import Layout from '../components/layout/Layout';
import AccessCodeModal from '../components/modals/AccessCodeModal';
import WheelDisplay from '../components/wheels/WheelDisplay';
import { useAuth } from '../hooks/useAuth';
import * as databaseIntegration from '../services/databaseIntegration';
import { SharedWheel, SharedWheelSpin, ShareStatus, Wheel, WheelEntry } from '../types/wheel';

// Export to make it a module
export {};

const SharedWheelView: React.FC = () => {
  const { shareId, accessCode } = useParams<Record<string, string | undefined>>();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [sharedWheel, setSharedWheel] = useState<SharedWheel | null>(null);
  const [parentWheel, setParentWheel] = useState<Wheel | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [spins, setSpins] = useState<SharedWheelSpin[]>([]);
  const [spinning, setSpinning] = useState<boolean>(false);
  const [shareStatus, setShareStatus] = useState<ShareStatus | null>(null);
  const [remainingSpins, setRemainingSpins] = useState<number | null>(null);
  const [externalSpin, setExternalSpin] = useState<boolean>(false);
  const [spinResult, setSpinResult] = useState<WheelEntry | null>(null);

  // Access code modal state
  const [showAccessCodeModal, setShowAccessCodeModal] = useState<boolean>(false);
  const [pendingWheel, setPendingWheel] = useState<SharedWheel | null>(null);
  const [accessCodeError, setAccessCodeError] = useState<string | null>(null);

  // Check if the current user is the owner of the wheel
  const isOwner = currentUser && sharedWheel?.ownerId === currentUser.uid;

  // Load the shared wheel data
  useEffect(() => {
    const loadSharedWheel = async () => {
      if (!shareId) {
        setError('Invalid share link');
        setLoading(false);
        return;
      }

      // If we already have a wheel loaded and we're just showing the access code modal,
      // don't reload the wheel data
      if (showAccessCodeModal && pendingWheel) {
        return;
      }

      try {
        // Get the shared wheel
        const wheel = await databaseIntegration.getSharedWheel(shareId);

        if (!wheel) {
          setError('Shared wheel not found');
          setLoading(false);
          return;
        }

        // Check if access code is required
        if (wheel.settings.accessCode) {
          // If access code is provided in URL and matches
          if (wheel.settings.accessCode === accessCode) {
            // Continue with the wheel loading
          } else {
            // Show the access code modal
            setPendingWheel(wheel);
            setShowAccessCodeModal(true);
            setLoading(false);
            return;
          }
        }

        // Set the shared wheel data
        setSharedWheel(wheel);

        // Calculate remaining spins
        if (wheel.settings.maxSpins !== null) {
          setRemainingSpins(wheel.settings.maxSpins - wheel.currentSpinCount);
        } else {
          setRemainingSpins(null); // Unlimited spins
        }

        // Load the parent wheel
        try {
          const parentWheelData = await databaseIntegration.getWheel(wheel.parentWheelId, true);
          if (parentWheelData) {
            setParentWheel(parentWheelData);
          }
        } catch (error) {
          console.error('Error loading parent wheel:', error);
        }

        // Get the share status
        const status = getShareStatus(wheel);
        setShareStatus(status);

        // Load spins if the wheel is valid
        if (status === ShareStatus.ACTIVE || isOwner) {
          const wheelSpins = await databaseIntegration.getSharedWheelSpins(shareId);
          setSpins(wheelSpins);
        }

        setLoading(false);
      } catch (error) {
        console.error('Error loading shared wheel:', error);
        setError('Failed to load shared wheel');
        setLoading(false);
      }
    };

    loadSharedWheel();
  }, [shareId, accessCode, currentUser, isOwner, showAccessCodeModal, pendingWheel]);

  // Handle access code submission
  const handleAccessCodeSubmit = (submittedCode: string) => {
    setAccessCodeError(null);

    if (pendingWheel && pendingWheel.settings.accessCode === submittedCode) {
      // Set loading to true while we fetch the data
      setLoading(true);

      // Calculate remaining spins
      if (pendingWheel.settings.maxSpins !== null) {
        setRemainingSpins(pendingWheel.settings.maxSpins - pendingWheel.currentSpinCount);
      } else {
        setRemainingSpins(null); // Unlimited spins
      }

      // Get the share status
      const status = getShareStatus(pendingWheel);
      setShareStatus(status);

      // Load the parent wheel and spins data in parallel
      Promise.all([
        databaseIntegration.getWheel(pendingWheel.parentWheelId, true),
        status === ShareStatus.ACTIVE || isOwner
          ? databaseIntegration.getSharedWheelSpins(pendingWheel.shareId)
          : Promise.resolve([]),
      ])
        .then(([parentWheelData, wheelSpins]) => {
          // Hide the modal first to prevent double rendering
          setShowAccessCodeModal(false);

          // Then set the wheel data
          setSharedWheel(pendingWheel);

          if (parentWheelData) {
            setParentWheel(parentWheelData);
          }

          if (wheelSpins.length > 0) {
            setSpins(wheelSpins);
          }

          // Finally, turn off loading
          setLoading(false);
        })
        .catch(error => {
          console.error('Error loading wheel data:', error);
          setError('Failed to load wheel data');
          setLoading(false);
        });
    } else {
      // Access code is incorrect
      setAccessCodeError('Invalid access code. Please try again.');
    }
  };

  // Handle access code modal cancel
  const handleAccessCodeCancel = () => {
    setShowAccessCodeModal(false);
    navigate('/');
  };

  // Determine the status of the shared wheel
  const getShareStatus = (wheel: SharedWheel): ShareStatus => {
    if (!wheel.settings.isActive) {
      return ShareStatus.INACTIVE;
    }

    if (wheel.settings.expiresAt) {
      const expiryDate =
        wheel.settings.expiresAt instanceof Date
          ? wheel.settings.expiresAt
          : wheel.settings.expiresAt.toDate();

      if (expiryDate < new Date()) {
        return ShareStatus.EXPIRED;
      }
    }

    if (wheel.settings.maxSpins !== null && wheel.currentSpinCount >= wheel.settings.maxSpins) {
      return ShareStatus.MAX_SPINS_REACHED;
    }

    return ShareStatus.ACTIVE;
  };

  // Handle spinning the wheel
  const handleSpinComplete = (entry: WheelEntry) => {
    if (!sharedWheel || !shareId || shareStatus !== ShareStatus.ACTIVE) {
      return;
    }

    setSpinning(true);
    setExternalSpin(false);
    setSpinResult(entry);

    try {
      // Create spin data
      const spinData: SharedWheelSpin = {
        id: uuidv4(),
        entryId: entry.id || '',
        entryText: entry.text,
        timestamp: new Date(),
        userId: currentUser?.uid || null, // Use null instead of undefined for unauthenticated users
      };

      // Record the spin
      databaseIntegration
        .spinSharedWheel(shareId, spinData)
        .then(() => {
          // Update local state
          setSpins([spinData, ...spins]);

          // Update remaining spins
          if (remainingSpins !== null) {
            setRemainingSpins(remainingSpins - 1);
          }

          // Update shared wheel data
          if (sharedWheel) {
            setSharedWheel({
              ...sharedWheel,
              currentSpinCount: sharedWheel.currentSpinCount + 1,
            });

            // Check if max spins reached
            if (
              sharedWheel.settings.maxSpins !== null &&
              sharedWheel.currentSpinCount + 1 >= sharedWheel.settings.maxSpins
            ) {
              setShareStatus(ShareStatus.MAX_SPINS_REACHED);
            }
          }
        })
        .catch(error => {
          console.error('Error spinning wheel:', error);
          setError('Failed to spin wheel');
        })
        .finally(() => {
          setSpinning(false);
        });
    } catch (error) {
      console.error('Error spinning wheel:', error);
      setError('Failed to spin wheel');
      setSpinning(false);
    }
  };

  // Render loading state
  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
            <p className="mt-2">Loading your dominant&apos;s wheel...</p>
          </div>
        </div>
      </Layout>
    );
  }

  // Render error state
  if (error) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8 flex justify-center">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
            <div className="mb-6 text-center">
              <svg
                width="80"
                height="80"
                viewBox="0 0 120 120"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="mx-auto"
              >
                <circle
                  cx="60"
                  cy="60"
                  r="54"
                  stroke="#EC4899"
                  strokeWidth="12"
                  strokeDasharray="15 15"
                />
                <circle cx="60" cy="60" r="30" fill="#8B5CF6" className="filter drop-shadow-lg" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4 text-center">
              Wheel Access Error
            </h2>
            <div
              className="bg-red-50 border-l-4 border-red-400 text-red-700 p-4 mb-6 rounded"
              role="alert"
            >
              <p className="font-medium">We couldn&apos;t access this wheel</p>
              <p className="mt-1">{error}</p>
            </div>
            <p className="text-gray-600 mb-6">
              {error === 'Shared wheel not found' &&
                'This shared wheel may have been deleted or the link might be incorrect.'}
              {error === 'Invalid access code' &&
                'You need the correct access code to view this wheel. Please contact the wheel owner.'}
              {error === 'Invalid share link' &&
                'The link you followed appears to be malformed or incomplete.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                className="bg-primary hover:bg-primary-dark text-white font-bold py-2 px-4 rounded transition-colors"
                onClick={() => navigate('/')}
              >
                Go to Home
              </button>
              <button
                className="bg-secondary hover:bg-secondary-dark text-white font-bold py-2 px-4 rounded transition-colors"
                onClick={() => navigate('/wheels')}
              >
                Explore Wheels
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Render access code modal
  if (showAccessCodeModal) {
    return (
      <Layout>
        <AccessCodeModal
          onSubmit={handleAccessCodeSubmit}
          onCancel={handleAccessCodeCancel}
          error={accessCodeError}
        />
      </Layout>
    );
  }

  // Render inactive/expired state
  if (shareStatus !== ShareStatus.ACTIVE && !isOwner) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div
            className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative"
            role="alert"
          >
            <strong className="font-bold">Notice: </strong>
            <span className="block sm:inline">
              {shareStatus === ShareStatus.INACTIVE && 'Your dominant has deactivated this wheel.'}
              {shareStatus === ShareStatus.EXPIRED &&
                'This wheel has expired. Contact your dominant for more access.'}
              {shareStatus === ShareStatus.MAX_SPINS_REACHED &&
                'Maximum number of spins reached. Beg your dominant for more spins.'}
            </span>
          </div>
          <div className="mt-4">
            <button
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
              onClick={() => navigate('/')}
            >
              Return to Home
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  // Render the shared wheel view
  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-dark mb-2">
            {isOwner ? 'Your Wheel You Have Shared' : 'Your Dominant&apos;s Wheel'}
          </h1>
          {sharedWheel && (
            <p className="text-gray-600">
              {isOwner
                ? 'You control this wheel for your submissives'
                : 'Your dominant has shared this wheel with you'}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Wheel Display */}
          {sharedWheel && parentWheel && (
            <div className="flex justify-center flex-col items-center">
              <WheelDisplay
                entries={parentWheel.entries}
                onSpinComplete={handleSpinComplete}
                size={400}
                colorScheme={parentWheel.theme}
                spinButtonText={isOwner ? 'Make Them Spin' : 'Spin Wheel'}
                showSpinButton={!isOwner && shareStatus === ShareStatus.ACTIVE}
                hideResultDisplay={false}
                externalSpin={externalSpin}
              />
            </div>
          )}

          {/* Wheel Information */}
          <div>
            {sharedWheel && (
              <div className="bg-white shadow-md rounded-lg p-6 mb-6">
                <h2 className="text-xl font-semibold mb-4">
                  {isOwner ? 'Control Information' : 'Submission Details'}
                </h2>

                {/* Status Badge */}
                <div className="mb-4">
                  <span
                    className={`inline-block px-3 py-1 rounded-full text-sm font-semibold mr-2 ${
                      shareStatus === ShareStatus.ACTIVE
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    {shareStatus === ShareStatus.ACTIVE && 'Active'}
                    {shareStatus === ShareStatus.INACTIVE && 'Inactive'}
                    {shareStatus === ShareStatus.EXPIRED && 'Expired'}
                    {shareStatus === ShareStatus.MAX_SPINS_REACHED && 'Max Spins Reached'}
                  </span>
                </div>

                {/* Spin Limit */}
                {sharedWheel.settings.maxSpins !== null && (
                  <div className="mb-4">
                    <p className="text-gray-700">
                      <span className="font-semibold">
                        {isOwner ? "Sub's Spin Limit:" : 'Your Spin Limit:'}
                      </span>{' '}
                      {sharedWheel.currentSpinCount} / {sharedWheel.settings.maxSpins}
                    </p>
                    <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
                      <div
                        className="bg-blue-600 h-2.5 rounded-full"
                        style={{
                          width: `${
                            (sharedWheel.currentSpinCount / sharedWheel.settings.maxSpins) * 100
                          }%`,
                        }}
                      ></div>
                    </div>
                  </div>
                )}

                {/* Expiration Date */}
                {sharedWheel.settings.expiresAt && (
                  <p className="text-gray-700 mb-4">
                    <span className="font-semibold">
                      {isOwner ? "Sub's Access Expires:" : 'Your Access Expires:'}
                    </span>{' '}
                    {sharedWheel.settings.expiresAt instanceof Date
                      ? sharedWheel.settings.expiresAt.toLocaleDateString()
                      : sharedWheel.settings.expiresAt.toDate().toLocaleDateString()}
                  </p>
                )}

                {/* External Spin Button (for owner) */}
                {isOwner && shareStatus === ShareStatus.ACTIVE && (
                  <button
                    className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50 mt-4"
                    onClick={() => setExternalSpin(true)}
                    disabled={spinning}
                  >
                    {spinning ? 'Spinning...' : 'Spin for them'}
                  </button>
                )}

                {/* Spin Result */}
                {spinResult && (
                  <div className="mt-4 p-4 border-2 border-primary rounded">
                    <h3 className="text-lg font-semibold mb-2">Latest Result:</h3>
                    <div className="text-xl font-bold text-primary">
                      {spinResult.emoji && <span className="mr-2">{spinResult.emoji}</span>}
                      {spinResult.text}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Spin History */}
            <div className="bg-white shadow-md rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-4">
                {isOwner ? "Sub's Spin History" : 'Your Submission History'}
              </h2>

              {spins.length === 0 ? (
                <p className="text-gray-600">
                  {isOwner ? "Your sub hasn't spun yet" : "You haven't submitted to a spin yet"}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white">
                    <thead>
                      <tr>
                        <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Result
                        </th>
                        <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Time
                        </th>
                        {isOwner && (
                          <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            User
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {spins.map(spin => (
                        <tr key={spin.id}>
                          <td className="py-2 px-4 border-b border-gray-200">{spin.entryText}</td>
                          <td className="py-2 px-4 border-b border-gray-200">
                            {spin.timestamp instanceof Date
                              ? spin.timestamp.toLocaleString()
                              : spin.timestamp.toDate().toLocaleString()}
                          </td>
                          {isOwner && (
                            <td className="py-2 px-4 border-b border-gray-200">
                              {spin.userId || 'Anonymous'}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Owner Actions */}
        {isOwner && (
          <div className="mt-6 container mx-auto px-4">
            <button
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded mr-2"
              onClick={() => navigate(`/wheels/${sharedWheel?.parentWheelId}`)}
            >
              View Your Original Wheel
            </button>
            <button
              className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
              onClick={() => navigate('/my-wheels')}
            >
              Back to My Wheels
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SharedWheelView;
